// ============================================================================
// DEVICE FLEET MANAGEMENT SERVICE
// API functions for remote device management (health, config, updates, SSH keys)
// ============================================================================

import { api } from './api';

export interface SystemMetrics {
  cpuPercent: number;
  memPercent: number;
  diskPercent: number;
  tempC: number;
  codeVersion: string;
  uptimeSeconds: number;
}

export interface ManagementCommand {
  commandId: string;
  deviceId: string;
  type: 'health-report' | 'update-config' | 'rotate-ssh-keys' | 'update-software' | 'restart-service' | 'set-volume';
  payload: Record<string, any>;
  status: 'pending' | 'completed' | 'error';
  queuedAt: string;
  completedAt?: string;
  result?: Record<string, any>;
}

export interface UploadPackageResponse {
  uploadUrl: string;
  bucket: string;
  key: string;
  expiresIn: number;
}

// Queue a management command for a device
export async function queueManagementCommand(
  deviceId: string,
  type: ManagementCommand['type'],
  payload: Record<string, any> = {}
): Promise<any> {
  const { data } = await api.post('/api/devices-n8n/management/queue', {
    deviceId,
    type,
    payload
  });
  return data;
}

// Get management command history for a device
export async function getManagementCommands(
  deviceId: string,
  status?: string,
  limit = 20
): Promise<{ commands: ManagementCommand[] }> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (status) params.set('status', status);
  const { data } = await api.get(`/api/devices-n8n/management/commands/${deviceId}?${params}`);
  return data;
}

// Request a health report from a device
export async function requestHealthReport(deviceId: string): Promise<any> {
  return queueManagementCommand(deviceId, 'health-report', {});
}

// Push a config update to a device
export async function pushConfigUpdate(
  deviceId: string,
  configPatch: Record<string, any>,
  requireRestart = false
): Promise<any> {
  return queueManagementCommand(deviceId, 'update-config', { configPatch, requireRestart });
}

// Rotate SSH keys on a device
export async function rotateSSHKeys(
  deviceId: string,
  authorizedKeys: string
): Promise<any> {
  const encoder = new TextEncoder();
  const data = encoder.encode(authorizedKeys);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const sha256 = Array.from(new Uint8Array(hashBuffer)).map(b => b.toString(16).padStart(2, '0')).join('');
  return queueManagementCommand(deviceId, 'rotate-ssh-keys', { authorizedKeys, sha256 });
}

// Restart a device's service
export async function restartDevice(deviceId: string): Promise<any> {
  return queueManagementCommand(deviceId, 'restart-service', {});
}

// Set speaker volume (0-100) on a device
export async function setDeviceVolume(
  deviceId: string,
  volumePercent: number
): Promise<any> {
  const clamped = Math.max(0, Math.min(100, Math.round(volumePercent)));
  return queueManagementCommand(deviceId, 'set-volume', { volumePercent: clamped });
}

// Deploy software update to multiple devices
export async function deployUpdate(
  deviceIds: string[],
  s3Url: string,
  version: string,
  sha256?: string,
  service?: 'camera-capture' | 'audio-capture'
): Promise<any> {
  const { data } = await api.post('/api/devices-n8n/management/update-all', {
    deviceIds,
    s3Url,
    version,
    sha256,
    ...(service && { service })
  });
  return data;
}

// Get presigned URL for uploading an update package
export async function getUploadPackageUrl(
  version: string,
  filename: string
): Promise<UploadPackageResponse> {
  const { data } = await api.post('/api/devices-n8n/management/upload-package', {
    version,
    filename
  });
  return data;
}

// Get latest health data for a device
export async function getDeviceHealth(deviceId: string): Promise<any> {
  const { data } = await api.get(`/api/devices-n8n/management/health/${deviceId}`);
  return data;
}

// Helper: format uptime
export function formatUptime(seconds: number): string {
  if (seconds < 0) return 'N/A';
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

// Helper: get metric color class based on value
export function getMetricColor(value: number, thresholds = { warn: 70, critical: 90 }): string {
  if (value < 0) return 'text-slate-400';
  if (value >= thresholds.critical) return 'text-red-600';
  if (value >= thresholds.warn) return 'text-amber-600';
  return 'text-emerald-600';
}

// Helper: get temp color
export function getTempColor(tempC: number): string {
  if (tempC < 0) return 'text-slate-400';
  if (tempC >= 80) return 'text-red-600';
  if (tempC >= 65) return 'text-amber-600';
  return 'text-emerald-600';
}
